/**
 * Cliente HTTP da conferência facial (E80.F2, ADR-0079) — raia própria.
 *
 * Espelha apps/api/types/conferencia_facial.py e
 * apps/api/runtime/conferencia_facial.py.
 *
 * Duas coisas que o contrato deliberadamente **não** traz: percentual e score
 * (§2.3/§2.5). Não são campos opcionais — não existem. Um número de opinião que
 * varia entre execuções, num papel que pode chegar a um processo, é munição de
 * contradição.
 *
 * A `leitura` vem RESOLVIDA do servidor: `null` é "ainda não conferido",
 * `nao_se_aplica` é "não há par escolhido". Derivá-la aqui deixaria a tela e o
 * backend livres para discordar sobre o mesmo estado (lição do veredito de
 * fonte única, E59).
 */
import type { AxiosInstance } from 'axios';

import { classifyExameEletronicoError } from '@/lib/exame_eletronico';
import type { GrupoDaFoto } from '@/lib/fotos_do_signatario';

/** O que uma conferência gravada pode dizer. */
export type LeituraDoParecer =
  | 'elementos_convergentes'
  | 'elementos_divergentes'
  | 'nao_foi_possivel_conferir';

/** O vocabulário completo da tela: o do parecer mais a ausência declarada. */
export type LeituraDaConferencia = LeituraDoParecer | 'nao_se_aplica';

export interface FotoConferidaData {
  foto_id: string;
  grupo: GrupoDaFoto;
  nome_arquivo: string;
  content_sha256: string;
}

export interface ParConferidoData {
  documento_identidade: FotoConferidaData;
  ccb: FotoConferidaData;
}

export interface ConferenciaFacialParecerData {
  id: string;
  case_id: string;
  created_by: string;
  par: ParConferidoData;
  leitura: LeituraDoParecer;
  convergencias: string[];
  divergencias: string[];
  ressalvas: string[];
  created_at: string;
}

export interface ConferenciaFacialData {
  case_id: string;
  tem_par_eleito: boolean;
  leitura: LeituraDaConferencia | null;
  parecer: ConferenciaFacialParecerData | null;
  /** O perito trocou a foto escolhida depois do parecer. */
  par_mudou: boolean;
}

function basePath(caseId: string): string {
  return `/casos/${caseId}/conferencia-facial`;
}

const LEITURAS_DO_PARECER: readonly string[] = [
  'elementos_convergentes',
  'elementos_divergentes',
  'nao_foi_possivel_conferir',
];

const LEITURAS_DA_TELA: readonly string[] = [...LEITURAS_DO_PARECER, 'nao_se_aplica'];

const SHA256_HEX = /^[0-9a-f]{64}$/;

function objeto(valor: unknown): Record<string, unknown> | null {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor)
    ? (valor as Record<string, unknown>)
    : null;
}

function listaDeTexto(valor: unknown): boolean {
  return Array.isArray(valor) && valor.every((item) => typeof item === 'string');
}

function ehFotoConferida(valor: unknown, grupo: GrupoDaFoto): boolean {
  const foto = objeto(valor);
  return (
    foto !== null &&
    typeof foto.foto_id === 'string' &&
    foto.grupo === grupo &&
    typeof foto.nome_arquivo === 'string' &&
    typeof foto.content_sha256 === 'string' &&
    SHA256_HEX.test(foto.content_sha256)
  );
}

function textosEm(alvo: Record<string, unknown>, chaves: readonly string[]): boolean {
  return chaves.every((chave) => typeof alvo[chave] === 'string');
}

function ehParDoParecer(valor: unknown): boolean {
  const par = objeto(valor);
  return (
    par !== null &&
    ehFotoConferida(par.documento_identidade, 'documento_identidade') &&
    ehFotoConferida(par.ccb, 'ccb')
  );
}

function ehParecer(valor: unknown): valor is ConferenciaFacialParecerData {
  const parecer = objeto(valor);
  if (parecer === null) return false;
  return (
    textosEm(parecer, ['id', 'case_id', 'created_by', 'created_at', 'leitura']) &&
    LEITURAS_DO_PARECER.includes(parecer.leitura as string) &&
    ['convergencias', 'divergencias', 'ressalvas'].every((campo) => listaDeTexto(parecer[campo])) &&
    ehParDoParecer(parecer.par)
  );
}

/**
 * Resposta que não é o contrato INTEIRO vira erro, e não um objeto meio-vazio.
 *
 * A primeira versão desta guarda conferia só `tem_par_eleito` (F-E80.F2-07), e
 * isso não continha nada: um 200 parcial chegava com `parecer: undefined`, que
 * `undefined !== null` encaminhava ao componente do parecer, e o acesso a
 * `parecer.leitura` derrubava a PÁGINA no Error Boundary — exatamente o cenário
 * que a guarda dizia manter dentro da seção. Drift de versão devolve um corpo
 * plausível, não um corpo obviamente errado; conferir um campo só é o mesmo que
 * não conferir.
 */
function ehContrato(data: unknown): data is ConferenciaFacialData {
  const estado = objeto(data);
  if (estado === null) return false;
  const leituraValida =
    estado.leitura === null ||
    (typeof estado.leitura === 'string' && LEITURAS_DA_TELA.includes(estado.leitura));
  return (
    typeof estado.case_id === 'string' &&
    typeof estado.tem_par_eleito === 'boolean' &&
    typeof estado.par_mudou === 'boolean' &&
    leituraValida &&
    (estado.parecer === null || ehParecer(estado.parecer))
  );
}

export async function lerConferenciaFacial(
  client: AxiosInstance,
  caseId: string,
): Promise<ConferenciaFacialData> {
  let data: unknown;
  try {
    ({ data } = await client.get<ConferenciaFacialData>(basePath(caseId)));
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
  if (!ehContrato(data)) throw classifyExameEletronicoError(new Error('resposta fora do contrato'));
  return data;
}

/**
 * Compara o par escolhido e devolve o parecer recém-gravado.
 *
 * O retorno é validado como o do GET: o corpo da escrita alimenta a mesma tela,
 * e deixá-lo passar sem conferência só mudaria de porta o objeto meio-vazio.
 */
export async function executarConferenciaFacial(
  client: AxiosInstance,
  caseId: string,
): Promise<ConferenciaFacialParecerData> {
  let data: unknown;
  try {
    ({ data } = await client.post<ConferenciaFacialParecerData>(basePath(caseId)));
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
  if (!ehParecer(data)) throw classifyExameEletronicoError(new Error('resposta fora do contrato'));
  return data;
}
